/**
 * Privacy Policy Screen
 * 
 * Displays the Privacy Policy for Tripvio.
 * This is an in-app version that can later be replaced with external URL links.
 */

import { Pressable, View, Platform, Linking } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Screen } from '@/components/layout/Screen';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import { useTheme } from '@/theme';

export default function PrivacyPolicyScreen() {
  const router = useRouter();
  const theme = useTheme();

  return (
    <Screen
      title="Privacy Policy"
      subtitle="Last updated: May 2026"
      headerAction={
        <Pressable
          onPress={() => router.back()}
          style={({ pressed }) => ({
            padding: theme.spacing(1),
            borderRadius: theme.radii.md,
            opacity: pressed ? 0.7 : 1,
          })}
          accessibilityLabel="Go back"
          accessibilityRole="button"
        >
          <Ionicons name="chevron-back" size={24} color={theme.colors.text} />
        </Pressable>
      }
    >
      <View style={{ gap: theme.spacing(3) }}>
        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Introduction</SubHeading>
          <Body color={theme.colors.textSecondary}>
            Tripvio ("we," "our," or "us") operates the Tripvio mobile application and website at tripvio.app. We are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Information We Collect</SubHeading>
          <Body color={theme.colors.textSecondary}>
            We collect information that you provide directly to us, including:
          </Body>
          <View style={{ gap: theme.spacing(1), paddingLeft: theme.spacing(2) }}>
            <Body color={theme.colors.textSecondary}>
              • Account information (name, email address, profile picture)
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Travel information (trips, itineraries, bookings, expenses)
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Email content (when you connect your email for trip detection)
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Documents (passports, visas, travel documents)
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Preferences and settings
            </Body>
          </View>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(1) }}>
            We also automatically collect certain information when you use our Service, including:
          </Body>
          <View style={{ gap: theme.spacing(1), paddingLeft: theme.spacing(2) }}>
            <Body color={theme.colors.textSecondary}>
              • Device information (device type, operating system, unique device identifiers)
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Usage data (features accessed, time spent, interactions)
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Location data (if you enable location services for map features)
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Log data (IP address, browser type, access times)
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Partner-link activity (for example, when you tap outbound partner or affiliate links, we may record that interaction for analytics, attribution, and reporting)
            </Body>
          </View>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>How We Use Your Information</SubHeading>
          <Body color={theme.colors.textSecondary}>
            We use the information we collect to:
          </Body>
          <View style={{ gap: theme.spacing(1), paddingLeft: theme.spacing(2) }}>
            <Body color={theme.colors.textSecondary}>
              • Provide, maintain, and improve our services
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Automatically detect and import trips from your email
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Personalize your experience and provide recommendations
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Process transactions and send related information
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Send you technical notices and support messages
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Respond to your comments and questions
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Measure engagement with partner offers and affiliate placements, and attribute referrals where applicable
            </Body>
          </View>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Data Security</SubHeading>
          <Body color={theme.colors.textSecondary}>
            We implement appropriate technical and organizational measures to protect your personal information. Your data is encrypted both in transit and at rest. We use industry-standard security practices to safeguard your information.
          </Body>
          <Body color={theme.colors.textSecondary}>
            However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Data Sharing</SubHeading>
          <Body color={theme.colors.textSecondary}>
            We do not sell your personal information. We may share your information only in the following circumstances:
          </Body>
          <View style={{ gap: theme.spacing(1), paddingLeft: theme.spacing(2) }}>
            <Body color={theme.colors.textSecondary}>
              • With your consent
            </Body>
            <Body color={theme.colors.textSecondary}>
              • With service providers who assist us in operating our services
            </Body>
            <Body color={theme.colors.textSecondary}>
              • With affiliate networks or partners when necessary to measure referrals, attribute commissions, or operate partner placements you interact with
            </Body>
            <Body color={theme.colors.textSecondary}>
              • To comply with legal obligations
            </Body>
            <Body color={theme.colors.textSecondary}>
              • To protect our rights and safety
            </Body>
          </View>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Your Rights</SubHeading>
          <Body color={theme.colors.textSecondary}>
            Depending on your location, you may have certain rights regarding your personal information, including:
          </Body>
          <View style={{ gap: theme.spacing(1), paddingLeft: theme.spacing(2) }}>
            <Body color={theme.colors.textSecondary}>
              • Access to your personal information
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Correction of inaccurate information
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Deletion of your information
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Data portability (receive your data in a structured format)
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Right to object to processing of your information
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Right to restrict processing of your information
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Right to withdraw consent (where processing is based on consent)
            </Body>
            <Body color={theme.colors.textSecondary}>
              • Right to opt-out of sale of personal information (California residents)
            </Body>
          </View>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(1) }}>
            To exercise these rights, please contact us at privacy@tripvio.app. We will respond to your request within 30 days, or as required by applicable law.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Email Access</SubHeading>
          <Body color={theme.colors.textSecondary}>
            When you connect your email account, we access your emails solely to detect travel-related bookings and automatically create trips in your account. We access only emails containing travel confirmations (flights, hotels, car rentals, etc.) and extract only the booking information necessary for trip planning. We do not read, store, or share the full content of your emails beyond what is necessary for trip detection. You may revoke email access at any time through your account settings.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Google Account and Gmail Access</SubHeading>
          <Body color={theme.colors.textSecondary}>
            TripVio only accesses Gmail content that is necessary to identify, extract, and organize travel-related booking confirmations, itinerary details, receipts, and reservation information explicitly requested by the user.
          </Body>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(1) }}>
            TripVio does not sell Gmail data, use Gmail data for advertising purposes, or use Gmail data for unrelated profiling, analytics, or marketing.
          </Body>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(1) }}>
            Google user data is only used to provide and improve the travel organization functionality within the TripVio platform.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Automated Processing and OCR</SubHeading>
          <Body color={theme.colors.textSecondary}>
            TripVio may use automated processing technologies, including optical character recognition (OCR), AI-assisted parsing, and document analysis tools to extract and organize travel-related information from uploaded files, receipts, itineraries, booking confirmations, and connected email accounts.
          </Body>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(1) }}>
            This processing is performed solely to provide core TripVio functionality such as itinerary organization, trip management, expense tracking, and travel document accessibility.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Data Retention and Account Deletion</SubHeading>
          <Body color={theme.colors.textSecondary}>
            Users may request deletion of their TripVio account and associated personal data at any time by contacting hello@tripvio.app.
          </Body>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(1) }}>
            TripVio retains data only for as long as necessary to provide services, comply with legal obligations, resolve disputes, and enforce agreements.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Data Breach Notification</SubHeading>
          <Body color={theme.colors.textSecondary}>
            In the event of a data breach that may affect your personal information, we will notify you and relevant authorities as required by applicable law, typically within 72 hours of becoming aware of the breach, where feasible.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Age Requirement</SubHeading>
          <Body color={theme.colors.textSecondary}>
            Tripvio is intended for users who are at least 18 years of age. We do not knowingly collect personal information from individuals under 18. If we become aware that we have collected personal information from someone under 18, we will take steps to delete such information promptly.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Changes to This Policy</SubHeading>
          <Body color={theme.colors.textSecondary}>
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Cookies and Tracking Technologies</SubHeading>
          <Body color={theme.colors.textSecondary}>
            We use cookies, web beacons, and similar tracking technologies to collect and store information about your use of our Service. These technologies help us analyze usage patterns, improve our services, and personalize your experience. We use both session cookies (which expire when you close your browser) and persistent cookies (which remain until deleted or expired).
          </Body>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
            You can control cookies through your browser settings. However, disabling cookies may limit your ability to use certain features of our Service. We use analytics services (such as Firebase Analytics) to understand how users interact with our Service. These services may use cookies and similar technologies to collect information about your use of our Service.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Third-Party Services</SubHeading>
          <Body color={theme.colors.textSecondary}>
            We use third-party services to provide our functionality, including Firebase for authentication and data storage, Google/Apple for sign-in, email providers for trip detection, and analytics services. These services have their own privacy policies governing data collection and use. We are not responsible for the privacy practices of these third-party services.
          </Body>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
            When you use third-party services through our Service (such as booking travel through external links), your interactions are governed by those third parties' terms and privacy policies. We encourage you to review the privacy policies of any third-party services you access through our Service.
          </Body>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
            When you leave Tripvio through an affiliate or partner link, the destination site or affiliate network may collect information according to its own policies (for example, cookies, device identifiers, or referral parameters used for commission reporting). Tripvio does not receive your full booking details from partners solely because you clicked a link unless you separately provide that information to us or integrate a connected service.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>International Data Transfers</SubHeading>
          <Body color={theme.colors.textSecondary}>
            Your information may be transferred to and processed in countries other than your country of residence, including the United States. These countries may have data protection laws that differ from those in your country. We implement appropriate safeguards, including standard contractual clauses approved by relevant authorities, to protect your information when transferred internationally. By using our Service, you consent to the transfer of your information to these countries.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Legal Basis for Processing (GDPR)</SubHeading>
          <Body color={theme.colors.textSecondary}>
            If you are located in the European Economic Area (EEA) or United Kingdom, we process your personal information based on the following legal bases: (1) your consent, (2) performance of a contract with you, (3) compliance with legal obligations, (4) protection of vital interests, (5) performance of a task carried out in the public interest, or (6) legitimate interests pursued by us or a third party.
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Data Controller Information</SubHeading>
          <Body color={theme.colors.textSecondary}>
            Tripvio is the data controller responsible for your personal information. For users in the European Economic Area (EEA) or United Kingdom, our representative for GDPR purposes can be contacted at:
          </Body>
          <Body color={theme.colors.primary} style={{ fontWeight: '600', marginTop: theme.spacing(0.5) }}>
            privacy@tripvio.app
          </Body>
        </View>

        <View style={{ gap: theme.spacing(1.5) }}>
          <SubHeading>Contact Us</SubHeading>
          <Body color={theme.colors.textSecondary}>
            If you have any questions about this Privacy Policy, wish to exercise your rights, or have concerns about how we handle your personal information, please contact us at:
          </Body>
          <Body color={theme.colors.primary} style={{ fontWeight: '600' }}>
            privacy@tripvio.app
          </Body>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
            Website: tripvio.app
          </Body>
          <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
            We will respond to your inquiry within 30 days, or as required by applicable law.
          </Body>
        </View>

        <View style={{ marginTop: theme.spacing(2), paddingTop: theme.spacing(2), borderTopWidth: 1, borderTopColor: theme.colors.borderMuted }}>
          <Pressable
            onPress={() => {
              const webUrl = 'https://tripvio.app/privacy';
              if (Platform.OS === 'web') {
                if (typeof window !== 'undefined') {
                  window.open(webUrl, '_blank');
                }
              } else {
                Linking.openURL(webUrl).catch((err) => {
                  console.error('Failed to open privacy policy URL:', err);
                });
              }
            }}
            style={({ pressed }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              gap: theme.spacing(1),
              padding: theme.spacing(1.5),
              borderRadius: theme.radii.md,
              backgroundColor: pressed ? theme.colors.surfaceVariant : 'transparent',
              opacity: pressed ? 0.7 : 1,
            })}
            accessibilityRole="link"
            accessibilityLabel="View Privacy Policy on web"
          >
            <Ionicons name="open-outline" size={18} color={theme.colors.primary} />
            <ThemedText variant="body" color={theme.colors.primary} style={{ textDecorationLine: 'underline' }}>
              View this policy on web
            </ThemedText>
          </Pressable>
        </View>
      </View>
    </Screen>
  );
}















